/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { NumberBox as DXNumberBox } from 'devextreme-react/number-box';
import { Validator, RequiredRule, CustomRule } from 'devextreme-react/validator';
import '../../../scss/controls/_numberbox.scss';
import { KeyUpEvent, ValueChangedEvent } from 'devextreme/ui/number_box';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { InfoIconButton } from '../InfoIconButton';

export enum NumberBoxComponentSpacing {
    None = 'noSpacing',
    SmallSpacing = 'smallSpacing',
    StandardSpacing = 'standardSpacing',
    LargeSpacing = 'largeSpacing',
}

interface NumberBoxProps {
    id?: string;
    dataTestid?: string;
    value?: number;
    allowNegative?: boolean;
    format?: string;
    readOnly?: boolean;
    disabled?: boolean;
    label?: string;
    helperText?: React.ReactNode;
    required?: boolean;
    requiredMessage?: string;
    children?: React.ReactNode;
    validator?: React.ReactNode;
    width?: string;
    min?: number;
    max?: number;
    mask?: string;
    maskInvalidMessage?: string;
    onChange: (value: number) => void;
    onEnterKey?: () => void;
    onKeyUp?: ((e: KeyUpEvent) => void) | undefined;
    validateValue?: (value: string) => boolean;
    onValidationEvent?: (isValid: boolean) => void;
    validateValueMessage?: string;
    componentSpacing?: NumberBoxComponentSpacing;
    // When true, commit on every keystroke (DX valueChangeEvent='input').
    // Default (false) preserves the existing behavior: a fresh box commits on
    // blur/Enter until first edit, then switches to live updates.
    liveUpdate?: boolean;
    showInfoIcon?: boolean;
    infoText?: string;
}

// Allowing "any" because it is defined by DevExtreme
export interface ValidationCallbackOptions {
    column: Record<string, any>;
    data: Record<string, any>;
    formItem: Record<string, any>;
    rule: Record<string, any>;
    validator: Record<string, any>;
    value: string | number;
}

export const NumberBox: React.FC<NumberBoxProps> = ({
    id,
    dataTestid,
    value,
    allowNegative = false,
    format = '#',
    mask,
    maskInvalidMessage,
    readOnly = false,
    disabled = false,
    label,
    helperText,
    required = false,
    requiredMessage = 'This field is required',
    children,
    validator,
    width = 'auto',
    min,
    max,
    onChange,
    onEnterKey,
    onKeyUp,
    validateValue,
    onValidationEvent,
    validateValueMessage,
    componentSpacing = NumberBoxComponentSpacing.StandardSpacing,
    liveUpdate = false,
    showInfoIcon = false,
    infoText,
}) => {
    const [valueChangeEvent, set_valueChangeEvent] = useState(liveUpdate ? 'input' : 'change');
    const [errorMessage, set_errorMessage] = useState('');

    const handleValueChanged = (e: ValueChangedEvent) => {
        onChange(e.value);
        set_valueChangeEvent('input');
    };

    const onValidated = (validatedInfo: ValidatedEvent) => {
        set_errorMessage(validatedInfo?.brokenRule?.message ?? '');
    };

    const validationCallback = (options: ValidationCallbackOptions): boolean => {
        if (!validateValue) return true;

        const result = validateValue(options.value.toString()) ?? false;

        if (onValidationEvent) onValidationEvent(result);

        return result;
    };

    const dxOnKeyUp = (e: any) => {
        if (onKeyUp) onKeyUp(e);

        if (!validateValue) return;
        if (!onValidationEvent) return;

        const value = e.event.currentTarget.value;
        const isValid: boolean = validateValue(value);
        onValidationEvent(isValid);
    };

    const determineMinimumValue = () => {
        if (allowNegative) {
            if (min) {
                return min;
            } else {
                return undefined;
            }
        } else {
            if (min !== undefined && min > 0) {
                return min;
            } else {
                return 0;
            }
        }
    };

    return (
        <div
            className={`numberbox-container ${componentSpacing}`}
            style={{ width: width }}
        >
            {label && (
                <div>
                    <label className={`numberbox-label ${errorMessage ? 'error' : ''}`}>{required ? label + ' *' : label}</label>
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            <div
                className='numberbox-wrapper'
                style={{ position: 'relative' }}
            >
                <DXNumberBox
                    id={id}
                    data-testid={dataTestid}
                    mode='number'
                    format={format}
                    mask={mask}
                    maskInvalidMessage={maskInvalidMessage}
                    value={value}
                    valueChangeEvent={valueChangeEvent}
                    disabled={disabled}
                    onValueChanged={handleValueChanged}
                    onKeyUp={dxOnKeyUp}
                    readOnly={readOnly}
                    className={`numberbox-input ${errorMessage ? 'error' : ''}`}
                    width={width}
                    min={determineMinimumValue()}
                    max={max}
                    onEnterKey={onEnterKey}
                    isValid={errorMessage == ''}
                >
                    <Validator onValidated={onValidated}>
                        {required && <RequiredRule message={requiredMessage} />}
                        {validator}
                        {validateValue && validationCallback && (
                            <CustomRule
                                validationCallback={validationCallback}
                                message={validateValueMessage}
                            />
                        )}
                    </Validator>
                </DXNumberBox>
            </div>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='numberbox-error'>{errorMessage}</div>
            ) : (
                helperText && <div className='numberbox-helper'>{helperText}</div>
            )}
            {children && <div className='numberbox-children'>{children}</div>}
        </div>
    );
};
